import { Fragment, useCallback, useEffect, useState } from "react";
import { apiRequest } from "../../lib/http.ts";

interface Offer {
  id: string;
  productTitle: string | null;
  store: string;
  status: string;
  currentPrice: number | null;
  coupon: string | null;
  affiliateLink: string | null;
  sourceChatId: string;
  createdAt: string;
  errorMessage: string | null;
}

export function OffersPage() {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [storeFilter, setStoreFilter] = useState("");
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [linkDraft, setLinkDraft] = useState<{ id: string; value: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      if (storeFilter) params.set("store", storeFilter);
      const result = await apiRequest<{ items: Offer[] }>(`/api/v1/offers?${params}`);
      setOffers(result.items ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao carregar ofertas.");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, storeFilter]);

  useEffect(() => {
    const debounce = setTimeout(load, 300);
    return () => clearTimeout(debounce);
  }, [load]);

  const action = async (id: string, action: string) => {
    setActionBusy(id + action);
    try {
      const token = localStorage.getItem("affiliate_flow_auth_token");
      await fetch(`/api/v1/offers/${id}/${action}`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      await load();
    } catch {
      setError("Erro na operação.");
    } finally {
      setActionBusy(null);
    }
  };

  const submitManualLink = async (id: string, affiliateLink: string) => {
    setActionBusy(id + "link");
    try {
      await apiRequest(`/api/v1/offers/${id}/affiliate-link`, {
        method: "POST",
        body: JSON.stringify({ affiliateLink }),
      });
      setLinkDraft(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar o link.");
    } finally {
      setActionBusy(null);
    }
  };

  const statusLabel: Record<string, string> = {
    pending: "Pendente",
    processing: "Processando",
    processed: "Processada",
    published: "Publicada",
    rejected: "Rejeitada",
    manual_review: "Revisão manual",
    failed: "Falha",
  };

  const statusColor: Record<string, string> = {
    pending: "bg-yellow-500/10 text-yellow-700",
    processing: "bg-blue-500/10 text-blue-700",
    processed: "bg-green-500/10 text-green-700",
    published: "bg-green-500/10 text-green-700",
    rejected: "bg-red-500/10 text-red-700",
    manual_review: "bg-orange-500/10 text-orange-700",
    failed: "bg-red-500/10 text-red-700",
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <input
          type="search"
          placeholder="Buscar por título, link..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="min-w-48 flex-1 rounded-md border bg-background px-3 py-2 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border bg-background px-3 py-2 text-sm"
        >
          <option value="">Todos os status</option>
          <option value="pending">Pendente</option>
          <option value="processed">Processada</option>
          <option value="published">Publicada</option>
          <option value="manual_review">Revisão manual</option>
          <option value="failed">Falha</option>
          <option value="rejected">Rejeitada</option>
        </select>
        <select
          value={storeFilter}
          onChange={(e) => setStoreFilter(e.target.value)}
          className="rounded-md border bg-background px-3 py-2 text-sm"
        >
          <option value="">Todas as lojas</option>
          <option value="amazon">Amazon</option>
          <option value="shopee">Shopee</option>
          <option value="mercadolivre">Mercado Livre</option>
        </select>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {error}
        </div>
      )}

      {loading
        ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        )
        : offers.length === 0
        ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            Nenhuma oferta encontrada.
          </div>
        )
        : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-3 py-2 font-medium">Produto</th>
                  <th className="px-3 py-2 font-medium">Loja</th>
                  <th className="px-3 py-2 font-medium">Preço</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {offers.map((offer) => (
                  <Fragment key={offer.id}>
                    <tr className="border-t">
                      <td className="max-w-48 truncate px-3 py-2">
                        {offer.productTitle || "Sem título"}
                      </td>
                      <td className="px-3 py-2 capitalize">{offer.store}</td>
                      <td className="px-3 py-2">
                        {offer.currentPrice
                          ? `R$ ${offer.currentPrice.toFixed(2).replace(".", ",")}`
                          : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            statusColor[offer.status] || "bg-muted"
                          }`}
                        >
                          {statusLabel[offer.status] || offer.status}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          {offer.status === "pending" && (
                            <button
                              onClick={() => action(offer.id, "approve")}
                              disabled={actionBusy === offer.id + "approve"}
                              className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50"
                            >
                              Aprovar
                            </button>
                          )}
                          {(offer.status === "manual_review" || offer.status === "failed") && (
                            <button
                              type="button"
                              onClick={() =>
                                setLinkDraft(
                                  linkDraft?.id === offer.id
                                    ? null
                                    : { id: offer.id, value: offer.affiliateLink ?? "" },
                                )}
                              className="rounded border px-2 py-1 text-xs"
                            >
                              Inserir link
                            </button>
                          )}
                          {offer.status === "failed" && (
                            <button
                              onClick={() => action(offer.id, "reprocess")}
                              disabled={actionBusy === offer.id + "reprocess"}
                              className="rounded bg-blue-600 px-2 py-1 text-xs text-white disabled:opacity-50"
                            >
                              Reprocessar
                            </button>
                          )}
                          {offer.status !== "published" && (
                            <button
                              onClick={() => action(offer.id, "cancel")}
                              disabled={actionBusy === offer.id + "cancel"}
                              className="rounded bg-destructive px-2 py-1 text-xs text-white disabled:opacity-50"
                            >
                              Cancelar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {linkDraft?.id === offer.id && (
                      <tr className="border-t bg-muted/40">
                        <td colSpan={5} className="px-3 py-2">
                          <form
                            className="flex flex-wrap gap-2"
                            onSubmit={(e) => {
                              e.preventDefault();
                              submitManualLink(offer.id, linkDraft.value.trim());
                            }}
                          >
                            <input
                              type="url"
                              required
                              placeholder="Link de afiliado gerado no portal oficial"
                              value={linkDraft.value}
                              onChange={(e) =>
                                setLinkDraft({ id: offer.id, value: e.target.value })}
                              className="min-w-64 flex-1 rounded-md border bg-background px-3 py-2 text-sm"
                            />
                            <button
                              type="submit"
                              disabled={actionBusy === offer.id + "link"}
                              className="rounded bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50"
                            >
                              Salvar link
                            </button>
                          </form>
                          {offer.errorMessage && (
                            <p className="mt-2 text-xs text-muted-foreground">
                              {offer.errorMessage}
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
